import React, { useCallback, useContext, useMemo } from 'react'
import ModalContext from 'contexts/ModalContext'
import TourV2DeparturesMonthsCalendar from './TourV2DeparturesMonthsCalendar'
import LayoutContainer from 'components/Common/LayoutContainer'
import TourV2DepartureTileList from './TourV2DepartureTileList'
import TourV2DepartureBookingModal from './TourV2DepartureBookingModal'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { areDeparturesSoldOut } from 'lib/tours/tourDepartureUtils'
import TourV2DeparturesSoldOutTile from './TourV2DeparturesSoldOutTile'

const smallCardLimit = 4

interface Props {
  offer: App.Tours.TourV2Offer;
  variation: App.Tours.TourV2OfferVariation;
  departures: Array<App.Tours.TourV2OfferDeparture>;
  travellers?: Array<App.Occupants>;
}

function TourV2DeparturesByYearList(props: Props) {
  const { offer, travellers, departures, variation } = props

  const showModal = useContext(ModalContext)

  const onTileSelect = useCallback((departure: App.Tours.TourV2OfferDeparture) => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultDeparture={departure}
      defaultStep={travellers ? 'departure' : 'travellers'}
    />)
  }, [offer, showModal, travellers])

  const onMonthSelect = useCallback((selectedDate: Date) => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultDate={selectedDate}
      defaultStep={travellers ? 'departure' : 'travellers'}
    />)
  }, [offer, showModal, travellers])

  const onUpdateTravellers = useCallback(() => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultStep="travellers"
    />)
  }, [offer, showModal])

  const soldOutReason = useMemo(() => {
    return areDeparturesSoldOut(departures, travellers)
  }, [departures, travellers])

  if (soldOutReason) {
    return <TourV2DeparturesSoldOutTile
      reason={soldOutReason}
      onUpdateTravelers={onUpdateTravellers}
    />
  }

  if (variation.departures.length <= smallCardLimit) {
    return <TourV2DepartureTileList
      departures={departures}
      onDepartureSelect={onTileSelect}
      offer={offer}
      travellers={travellers}
      variation={variation}
    />
  }

  return <LayoutContainer>
    <TourV2DeparturesMonthsCalendar
      onSelect={onMonthSelect}
      offer={offer}
      departures={departures}
      travellers={travellers}
    />
  </LayoutContainer>
}

type MappedProps = Pick<Props, 'travellers' | 'variation'>

export default withOfferPageState((state): MappedProps => ({
  travellers: state.rooms,
  variation: state.tourV2Variation!,
}))(TourV2DeparturesByYearList)
